import React, { useEffect, useState } from 'react';
import { formatMoney, useCurrency } from '../context/CurrencyContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Calendar, Users, MapPin, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatYen = (amount, cur) => formatMoney(amount, cur);

export default function BookingSuccess() {
  useCurrency(); // re-render quand la devise d'affichage change
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const sessionId = searchParams.get('session_id');
  const bookingIdParam = searchParams.get('booking_id');

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [verified, setVerified] = useState(false);

  const dateLocaleMap = { fr: 'fr-FR', en: 'en-US', jp: 'ja-JP' };
  const dateLocale = dateLocaleMap[i18n.language] || 'en-US';

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else if (bookingIdParam) {
      fetchBooking(bookingIdParam);
    } else {
      setLoading(false);
    }
  }, [sessionId, bookingIdParam]);

  const verifyPayment = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/verify-payment/${sessionId}`);
      setVerified(response.data.payment_status === 'paid');
      if (response.data.booking_id) {
        await fetchBooking(response.data.booking_id);
      }
    } catch (error) {
      console.error('Verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBooking = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/v2/${bookingId}`, {
        withCredentials: true
      });
      setBooking(response.data);
      setVerified(response.data.payment_status === 'paid');
    } catch (error) {
      console.error('Booking fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#F9004D] animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">{t('bookingSuccess.verifying')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="booking-success">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <Card className="bg-zinc-900/50 border-white/10 p-6 sm:p-10 text-center">
          {verified ? (
            <>
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>

              <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t('bookingSuccess.confirmed')}
              </h1>

              <p className="text-zinc-400 mb-8">
                {t('bookingSuccess.thanks')}
              </p>

              {booking && (
                <div className="bg-zinc-800/50 rounded-xl p-6 text-left mb-8">
                  <h3 className="text-lg font-bold text-white mb-4">{t('bookingSuccess.details')}</h3>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-[#F9004D]" />
                      <div>
                        <span className="text-zinc-400 text-sm">{t('bookingSuccess.date')}</span>
                        <p className="text-white">
                          {new Date(booking.start_date).toLocaleDateString(dateLocale, {
                            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                          })}
                        </p>
                        <p className="text-[#00F0FF] text-sm">
                          {booking.number_of_days} {booking.number_of_days > 1 ? t('bookingSuccess.days') : t('bookingSuccess.day')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-[#F9004D]" />
                      <div>
                        <span className="text-zinc-400 text-sm">{t('bookingSuccess.participants')}</span>
                        <p className="text-white">
                          {booking.participants?.length} {booking.participants?.length > 1 ? t('bookingSuccess.persons') : t('bookingSuccess.person')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-[#F9004D]" />
                      <div>
                        <span className="text-zinc-400 text-sm">{t('bookingSuccess.host')}</span>
                        <p className="text-white">{booking.guide_name}</p>
                      </div>
                    </div>

                    <div className="border-t border-white/10 pt-3 mt-3">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">{t('bookingSuccess.totalPaid')}</span>
                        <span className="text-2xl font-bold text-[#F9004D]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {formatYen(booking.total_price)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#F9004D] hover:bg-[#D00040] text-white"
                >
                  {t('bookingSuccess.viewBookings')}
                </Button>
                <Button
                  onClick={() => navigate('/hosts')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {t('bookingSuccess.explore')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="h-12 w-12 text-amber-500" />
              </div>

              <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t('bookingSuccess.pending')}
              </h1>

              <p className="text-zinc-400 mb-8">
                {t('bookingSuccess.pendingDesc')}
              </p>

              <Button
                onClick={() => window.location.reload()}
                className="bg-[#F9004D] hover:bg-[#D00040] text-white"
              >
                {t('bookingSuccess.checkStatus')}
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
